import { useState } from 'react';
import type { ConsentRecord } from '@salonomy/types';
import { Button, Pill, Spinner } from '@salonomy/ui';
import { useClientConsents } from '../../data/consent.js';
import { dateFormat } from '../../lib/locale.js';
import { CaptureConsentModal } from './CaptureConsentModal.js';
import { ConsentRecordModal } from './ConsentRecordModal.js';
import { CONSENT_KIND_LABEL, recordValidity } from './consent-shared.js';

const DATE_FMT = dateFormat({
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** The newest patch-test record (records arrive newest-signed first), if any — drives the prominent badge. */
function newestPatchTest(records: ConsentRecord[]): ConsentRecord | undefined {
  return records.find((r) => r.kind === 'patch_test');
}

/**
 * WS-13 Feature 4 — the "Consent & consultations" section on a client's profile. Lists the client's
 * signed consent_records (kind + signed date + a derived validity badge), surfaces patch-test validity
 * PROMINENTLY at the top (the booking liability), and opens:
 *   - a CAPTURE flow (staff-level): pick an active template → fill → typed-name + explicit "I confirm"
 *     checkbox → POST /v1/clients/:clientId/consents;
 *   - a read-only viewer for any record showing the FROZEN verbatim snapshot wording.
 *
 * Capturing is staff-level (any signed-in staff role), so unlike the manager-gated settings panel this
 * always shows its Capture button. Records are immutable once signed — there is no edit/delete here.
 */
export function ConsentPanel({ clientId, clientName }: { clientId: string; clientName: string }) {
  const { data, isLoading, isError } = useClientConsents(clientId);
  const [capturing, setCapturing] = useState(false);
  const [viewing, setViewing] = useState<ConsentRecord | null>(null);

  const records = data ?? [];
  const patchTest = newestPatchTest(records);
  const patchValidity = patchTest ? recordValidity(patchTest) : null;

  return (
    <div className="client-detail__block">
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <span className="slm-stat__label">Consent &amp; consultations</span>
        <Button size="sm" variant="outline" onClick={() => setCapturing(true)}>
          Capture
        </Button>
      </div>

      {/* Patch-test validity, prominently — it's the booking gate. Shown whenever any patch_test exists. */}
      {patchTest ? (
        <div style={{ marginTop: 10 }}>
          {patchValidity === 'valid' ? (
            <Pill tone="success">
              Patch test valid
              {patchTest.validUntil
                ? ` until ${DATE_FMT.format(new Date(`${patchTest.validUntil}T00:00:00Z`))}`
                : ''}
            </Pill>
          ) : patchValidity === 'expired' ? (
            <Pill tone="danger">
              Patch test expired
              {patchTest.validUntil
                ? ` on ${DATE_FMT.format(new Date(`${patchTest.validUntil}T00:00:00Z`))}`
                : ''}
            </Pill>
          ) : (
            <Pill tone="soft">Patch test on file (no expiry)</Pill>
          )}
        </div>
      ) : null}

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <Spinner size={14} />
          <span className="muted" style={{ fontSize: 12.5 }}>
            Loading consent records…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
          Couldn&rsquo;t load this client&rsquo;s consent records.
        </div>
      ) : records.length === 0 ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
          No consent or consultation forms captured yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
          {records.map((r) => {
            const validity = recordValidity(r);
            return (
              <button
                key={r.id}
                className="till__line"
                style={{
                  fontSize: 13,
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  padding: '4px 0',
                  cursor: 'pointer',
                  width: '100%',
                }}
                onClick={() => setViewing(r)}
              >
                <span>
                  <span style={{ fontWeight: 600 }}>
                    {r.snapshotTitle || CONSENT_KIND_LABEL[r.kind]}
                  </span>
                  <span className="muted">
                    {' '}
                    · {CONSENT_KIND_LABEL[r.kind]} · {DATE_FMT.format(new Date(r.signedAt))}
                  </span>
                </span>
                {validity === 'valid' ? (
                  <Pill tone="success">Valid</Pill>
                ) : validity === 'expired' ? (
                  <Pill tone="danger">Expired</Pill>
                ) : (
                  <span className="muted" style={{ fontSize: 12 }}>
                    View
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {capturing ? (
        <CaptureConsentModal
          clientId={clientId}
          clientName={clientName}
          onClose={() => setCapturing(false)}
        />
      ) : null}

      {viewing ? <ConsentRecordModal record={viewing} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}
